/**
 * TOSS Web Application — is the on-screen keyboard up, and насколько?
 *
 * One question, asked once for the whole window: has the software keyboard taken the bottom of
 * the screen — и сколько именно она забрала. What asks it is chrome pinned to that edge — the portal's bottom bar — which has
 * to get out of the way while someone types, exactly as the bar in «Сметы» does
 * (`main_shell.dart`: the navbar collapses on `viewInsets.bottom > 0` and comes back after).
 *
 * **`visualViewport`, not a resize listener.** The two viewports diverge precisely in this
 * case, and only one of them notices:
 *
 *   - iOS Safari does NOT resize the layout viewport for the keyboard. `window.innerHeight`
 *     stays what it was, no `resize` fires, and a bar positioned `fixed` keeps its place in a
 *     layout viewport that is now partly behind the keyboard.
 *   - `visualViewport.height` is the part actually on screen, and it shrinks on both platforms.
 *
 * The difference of the two is therefore the keyboard's height (plus, on some browsers, a
 * collapsed address bar), which is why the threshold below is generous: a URL bar hiding on
 * scroll costs ~60px, a keyboard never less than ~200.
 *
 * `scroll` is listened to beside `resize` on purpose. iOS scrolls the visual viewport up to
 * keep the focused field visible, and the height that arrives with the first `resize` is
 * occasionally the pre-scroll one — the same object fires `scroll` right after with the
 * settled geometry.
 *
 * Returns `false` and subscribes to nothing where `visualViewport` is absent (jsdom, older
 * browsers): the caller then behaves exactly as it did before this hook existed.
 */

import { useEffect, useState } from 'react';

// ============================================================================
// Constants
// ============================================================================

/**
 * How much of the visual viewport has to disappear before we call it a keyboard.
 *
 * Above every browser-chrome effect (a hiding address bar is ~60px) and far below any real
 * keyboard (~260px on a phone, ~200 with a hardware one attached and only the suggestion strip
 * showing).
 */
const KEYBOARD_MIN_HEIGHT = 150;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Насколько клавиатура перекрывает низ окна, в пикселях CSS. `0` — клавиатуры нет.
 *
 * Это то число, на которое надо поднять элемент `position: fixed`, прибитый к низу, чтобы он
 * оказался НАД клавиатурой. Отсюда и `offsetTop` в формуле: `fixed` считается от layout viewport,
 * а iOS вдобавок сдвигает визуальный вьюпорт вверх, чтобы показать поле в фокусе, — без этого
 * слагаемого поднятая кнопка уезжает на высоту сдвига мимо.
 *
 * Ниже порога возвращает ноль: адресная строка браузера, уезжающая при прокрутке, стоит ~60 px, и
 * без порога кнопка дёргалась бы на каждой прокрутке страницы.
 */
export function useVirtualKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const measure = (): void => {
      // `?? 0` — не перестраховка: `offsetTop` появился в спецификации позже самого
      // `visualViewport`, и без него всё выражение стало бы NaN, то есть «клавиатуры нет».
      const hidden = window.innerHeight - viewport.height - (viewport.offsetTop ?? 0);
      // Округление, а не сырое число: на Android `resize` приходит на каждое нажатие, дробная
      // часть там гуляет, и без него компонент перерисовывался бы от 0.5 px.
      const next = hidden > KEYBOARD_MIN_HEIGHT ? Math.round(hidden) : 0;
      setInset((previous) => (previous === next ? previous : next));
    };

    measure();
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);
    return () => {
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
    };
  }, []);

  return inset;
}

/**
 * Whether the on-screen keyboard currently covers the bottom of the window.
 *
 * Тот же замер, что и у {@link useVirtualKeyboardInset}, сведённый к «да/нет»: это всё, что нужно
 * панели, которой достаточно уехать за край.
 */
export function useVirtualKeyboardOpen(): boolean {
  return useVirtualKeyboardInset() > 0;
}

export default useVirtualKeyboardOpen;
